import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getLoadingGroups, assignOrdersToContainer } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Users, Buildings, Package, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const GroupCard = ({ group, label, onBulkAssign }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border rounded-md p-3" data-testid={`group-card-${label}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-sm" data-testid={`group-label-${label}`}>{label}</h3>
          {group.company_name && <span className="text-xs text-muted-foreground">({group.company_name})</span>}
          <Badge variant="secondary" className="text-[10px]">{group.order_count} orders</Badge>
          <Badge className="bg-primary text-primary-foreground text-[10px]">{group.total_cars} cars</Badge>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setExpanded(!expanded)} data-testid={`group-expand-${label}`}>
            {expanded ? 'Hide' : 'View'} cars
          </Button>
          <Button size="sm" onClick={() => onBulkAssign(group)} data-testid={`group-bulk-assign-${label}`}>
            <Package className="w-4 h-4 mr-1" /> Assign to container
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="border-t pt-2 mt-2 space-y-2">
          {group.orders.map(o => (
            <div key={o.order_id} className="bg-slate-50 rounded px-2 py-1.5">
              <div className="flex items-center justify-between text-xs mb-1">
                <Link to={`/orders?order=${o.order_id}`} className="font-mono font-semibold text-primary hover:underline">
                  {o.order_number}
                </Link>
                <span className="text-muted-foreground">{o.client_name} · {o.cars_count} car{o.cars_count > 1 ? 's' : ''}</span>
              </div>
              <div className="grid grid-cols-2 gap-1">
                {o.cars.map(c => (
                  <div key={c.id} className="text-[11px] text-muted-foreground flex justify-between border-l-2 border-slate-300 pl-2">
                    <span>{c.brand} {c.model}{c.color ? ` · ${c.color}` : ''}</span>
                    {c.vin && <span className="font-mono">{c.vin.slice(-6)}</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const LoadingGroupsPage = () => {
  const [groups, setGroups] = useState({ dealer_groups: [], surname_groups: [] });
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [form, setForm] = useState({ container_number: '', shipping_company: '', vessel_number: '', bl_number: '', eta: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLoadingGroups();
      setGroups(res.data || { dealer_groups: [], surname_groups: [] });
    } catch (err) {
      toast.error('Failed to load loading groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openBulkAssign = (group) => {
    setActiveGroup(group);
    setForm({ container_number: '', shipping_company: '', vessel_number: '', bl_number: '', eta: '' });
    setAssignOpen(true);
  };

  const submitAssign = async (e) => {
    e.preventDefault();
    if (!form.container_number.trim()) {
      toast.error('Container number is required');
      return;
    }
    const orderIds = activeGroup.orders.map(o => o.order_id);
    setSaving(true);
    try {
      const res = await assignOrdersToContainer({
        container_number: form.container_number.trim(),
        order_ids: orderIds,
        shipping_company: form.shipping_company || undefined,
        vessel_number: form.vessel_number || undefined,
        bl_number: form.bl_number || undefined,
        eta: form.eta || undefined,
      });
      toast.success(`Container ${res.data.container_number} created · ${res.data.cars_updated} cars assigned`);
      setAssignOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally {
      setSaving(false);
    }
  };

  const totalGroups = (groups.dealer_groups?.length || 0) + (groups.surname_groups?.length || 0);

  return (
    <div className="space-y-4" data-testid="loading-groups-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Package weight="duotone" className="w-7 h-7 text-primary" />
            Loading Groups
          </h1>
          <p className="text-sm text-muted-foreground">Cars from the same dealer or family — bulk-assign to a single container for easier customs clearance.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-groups-btn">
          <ArrowsClockwise className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : totalGroups === 0 ? (
        <div className="bg-white border rounded-md p-10 text-center">
          <Package weight="duotone" className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="font-medium">No loading groups yet</p>
          <p className="text-sm text-muted-foreground mt-1">Groups appear when 2+ active orders share the same dealer or family surname.</p>
        </div>
      ) : (
        <Tabs defaultValue="dealers">
          <TabsList>
            <TabsTrigger value="dealers" data-testid="tab-dealers">
              <Buildings className="w-4 h-4 mr-1" /> Dealers ({groups.dealer_groups?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="families" data-testid="tab-families">
              <Users className="w-4 h-4 mr-1" /> Families ({groups.surname_groups?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="dealers" className="space-y-3 mt-3">
            {groups.dealer_groups?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No dealer groups with 2+ active orders.</p>
            ) : groups.dealer_groups.map(g => (
              <GroupCard key={`dealer-${g.client_id}`} group={g} label={g.client_name || 'Dealer'} onBulkAssign={openBulkAssign} />
            ))}
          </TabsContent>

          <TabsContent value="families" className="space-y-3 mt-3">
            {groups.surname_groups?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No family groups with 2+ active orders.</p>
            ) : groups.surname_groups.map(g => (
              <GroupCard key={`surname-${g.surname}`} group={g} label={g.surname} onBulkAssign={openBulkAssign} />
            ))}
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Assign to Container</DialogTitle>
          </DialogHeader>
          {activeGroup && (
            <p className="text-xs text-muted-foreground">
              Will assign <strong>{activeGroup.total_cars} cars</strong> from <strong>{activeGroup.order_count} orders</strong> to this container.
            </p>
          )}
          <form onSubmit={submitAssign} className="space-y-3">
            <div>
              <Label className="text-xs">Container Number *</Label>
              <Input value={form.container_number} onChange={(e) => setForm({ ...form, container_number: e.target.value })} placeholder="e.g. CMAU1234567" className="font-mono" data-testid="bulk-container-input" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Shipping Company</Label>
                <Input value={form.shipping_company} onChange={(e) => setForm({ ...form, shipping_company: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Vessel #</Label>
                <Input value={form.vessel_number} onChange={(e) => setForm({ ...form, vessel_number: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">B/L #</Label>
                <Input value={form.bl_number} onChange={(e) => setForm({ ...form, bl_number: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">ETA</Label>
                <Input type="date" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="bulk-assign-submit">
                {saving ? 'Assigning…' : 'Assign'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoadingGroupsPage;
